import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { RouteLocation, RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
import { RouteMetaPermission } from '@/models/permission';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n, { AllowedTranslationVerticals, loadTranslations } from '@/plugins/i18n';

import { toNonVueRoute } from './utils';

const Dashboard = () => import('@/pages/Company/Dashboard/Dashboard.vue');
const BuyerDashboardOMS = () => import('@/pages/BuyerDashboardOMS/BuyerDashboardOMS.vue');

const showBuyerDashboardOMS = ref<boolean>();

async function beforeEnter() {
  const { userActiveCompany: activeCompany } = storeToRefs(useUserStore());

  const verticals: AllowedTranslationVerticals[] = ['company'];

  if (activeCompany.value?.seller) {
    verticals.push('crm');
  }

  if (activeCompany.value?.buyer) {
    verticals.push('recentlyPurchased', 'shop', 'productRecommendation');
  }

  showBuyerDashboardOMS.value =
    useUserStore().getIsSplitEnabled(SPLIT_NAMES.BUYER_DASHBOARD_OMS) && activeCompany.value?.buyer;

  if (showBuyerDashboardOMS.value) {
    verticals.push('orders');
  }

  await Promise.all(verticals.map((vertical) => loadTranslations(vertical)));
}

const messagesDashboardRedirect: RouteMetaPermission = {
  rules: [
    ({ userActiveCompany, permissions }) => {
      if (userActiveCompany?.seller && permissions?.canManageOrdersReceived) {
        return true;
      }

      if (userActiveCompany?.buyer && permissions?.canRequestSamples) {
        return true;
      }

      // Also, if the company is neither a buyer nor a seller, it is a BME company and should be redirected to the Dashboard.
      return false;
    },
  ],
  redirect: (to) => ({ name: ROUTE_NAMES.COMPANY_DASHBOARD, params: { company: to.params.company } }),
};

const companyRoutes: RouteRecordRaw[] = [
  // Legacy routes

  {
    path: '',
    name: ROUTE_NAMES.COMPANY_DASHBOARD,
    component: () => (showBuyerDashboardOMS.value ? BuyerDashboardOMS() : Dashboard()),
    beforeEnter,
    meta: {
      segmentPageName: 'Dashboard',
      title: i18n.t('company.meta.title'),
      permissions: [
        {
          rules: [
            ({ userActiveCompany }) => {
              if (showBuyerDashboardOMS.value) {
                return Boolean(userActiveCompany?.buyer) && Boolean(userActiveCompany?.staffId);
              } else {
                return Boolean(userActiveCompany?.staffId);
              }
            },
          ],
        },
      ],
    },
  },
  {
    path: 'messages/',
    name: ROUTE_NAMES.MESSAGES,
    component: () => import('@/pages/Messages/Messages.vue'),
    meta: {
      title: () => i18n.t('common.messages'),
      permissions: [
        {
          rules: ['vue_messages_page'],
          redirect: (to) => ({ name: ROUTE_NAMES.DISCUSSIONS, params: { company: to.params.company } }),
        },
        messagesDashboardRedirect,
        { rules: [({ userActiveCompany }) => Boolean(userActiveCompany?.canDiscuss)] },
      ],
    },
  },

  toNonVueRoute({
    path: 'discussions/',
    name: ROUTE_NAMES.DISCUSSIONS,
    beforeEnter(to: RouteLocation, _from, next) {
      // If the split is enabled, redirect to the new messages page since the Vuefied page experience
      // for Messages is covered via `/<company-slug>/messages` instead of `/<company-slug>/discussions`
      if (useUserStore().getIsSplitEnabled(SPLIT_NAMES.VUE_MESSAGES_PAGE)) {
        // Redirects user: /<company-slug>/discussions -> /<company-slug>/messages
        return next({ name: ROUTE_NAMES.MESSAGES, params: { company: to.params.company } });
      }
      // Otherwise, continue to the legacy Messages page - AKA `Discussions`
      return next();
    },
    meta: {
      permissions: [messagesDashboardRedirect],
    },
  }),
];

export default companyRoutes;
